// Functions that create, update and delete widget data in FireStore

import { 
    collection, 
    doc, 
    getDocs, 
    getDoc, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    query, 
    where, 
    orderBy, 
    limit,
    serverTimestamp 
  } from 'firebase/firestore';
  import { 
    ref, 
    uploadBytes, 
    getDownloadURL, 
    deleteObject 
  } from 'firebase/storage';
  import { db, storage } from '../firebase/config';
  
  // Fetch all widgets
  export const getWidgets = async () => {
    try {
      console.log('Fetching widgets from Firestore...');
      const widgetsRef = collection(db, 'widgets');
      const snapshot = await getDocs(widgetsRef);
      console.log('Raw snapshot:', snapshot);
      console.log('Number of documents:', snapshot.docs.length);
      
      const allWidgets = snapshot.docs.map(doc => {
        const data = doc.data();
        // console.log('Widget document:', doc.id, data);
        return {
          id: doc.id,
          ...data
        };
      });
      
      // console.log('All widgets:', allWidgets);
      return allWidgets;
    } catch (error) {
      console.error('Error fetching published widgets:', error);
      return [];
    }
  };
  
  // Fetch all widgets (for admin dashboard)
  export const getAllWidgets = async (field = 'createdAt', direction = 'desc') => {
    try {
      const widgetsRef = collection(db, 'widgets');
      let snapshot;
      try {
        const q = query(widgetsRef, orderBy(field, direction));
        snapshot = await getDocs(q);
      } catch (orderError) {
        // If ordering fails, get without ordering
        snapshot = await getDocs(widgetsRef);
      }
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching all widgets:', error);
      // If collection doesn't exist yet or permission error, return empty array
      if (error.code === 'permission-denied' || 
          error.message?.includes('collection') ||
          error.message?.includes('Missing or insufficient permissions')) {
        return [];
      }
      return []; // Return empty array for other errors too
    }
  };
  
  // Fetch single widget by slug
  export const getWidgetBySlug = async (slug) => {
    try {
      const widgetsRef = collection(db, 'widgets');
      const q = query(widgetsRef, where('slug', '==', slug.toLowerCase()), limit(1));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      console.error('Error fetching widget by slug:', error);
      throw error;
    }
  };

  // Fetch single widget by title
  export const getWidgetByTitle = async (title) => {
    try {
      const widgetsRef = collection(db, 'widgets');
      const q = query(widgetsRef, where('title', '==', title), limit(1));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }
      
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      console.error('Error fetching widget by title:', error);
      throw error;
    }
  };
  
  // Fetch single widget by ID
  export const getWidgetById = async (id) => {
    try {
      const widgetRef = doc(db, 'widgets', id);
      const snapshot = await getDoc(widgetRef);
      
      if (!snapshot.exists()) {
        return null;
      }
      
      return {
        id: snapshot.id,
        ...snapshot.data()
      };
    } catch (error) {
      console.error('Error fetching widget by ID:', error);
      throw error;
    }
  };
  
  // Create new widget
  export const createWidget = async (widgetData) => {
    try {
      // Check if title already exists
      const existingWidget = await getWidgetByTitle(widgetData.title);
      if (existingWidget) {
        throw new Error('A widget with this title already exists. Please choose a different title.');
      }
      
      const newWidget = {
        ...widgetData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const widgetsRef = collection(db, 'widgets');
      const docRef = await addDoc(widgetsRef, newWidget);
      
      return {
        id: docRef.id,
        ...newWidget
      };
    } catch (error) {
      console.error('Error creating widget:', error);
      throw error;
    }
  };
  
  // Update existing widget
  export const updateWidget = async (id, widgetData) => {
    try {
      const widgetRef = doc(db, 'widgets', id);
      
      // If title changed
      let updateData = { ...widgetData };
      if (widgetData.title) {
        // Check if new title conflicts with existing widget (excluding current one)
        const existingWidget = await getWidgetByTitle(widgetData.title);
        if (existingWidget && existingWidget.id !== id) {
          throw new Error('A widget with this title already exists. Please choose a different title.');
        }
      }
      
      updateData.updatedAt = serverTimestamp();
      
      await updateDoc(widgetRef, updateData);
      
      // Return updated widget
      return await getWidgetById(id);
    } catch (error) {
      console.error('Error updating widget:', error);
      throw error;
    }
  };
  
  // Delete widget
  export const deleteWidget = async (id) => {
    try {
      const widgetRef = doc(db, 'widgets', id);
      
      // Get widget data to check for featured image
      const widget = await getWidgetById(id);
      
      // Delete icon if it exists
      if (widget?.icon) {
        try {
          await deleteWidgetImage(widget.icon);
        } catch (imageError) {
          console.warn('Could not delete icon:', imageError);
        }
      }
      
      await deleteDoc(widgetRef);
    } catch (error) {
      console.error('Error deleting widget:', error);
      throw error;
    }
  };
  
  // Upload widget image to Firebase Storage
  export const uploadWidgetImage = async (file) => {
    try {
      console.log('Starting image upload...', { file: file.name, size: file.size, type: file.type });
      
      if (!file) {
        throw new Error('No file provided');
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image must be smaller than 5MB');
      }
      
      // Generate unique filename
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      console.log('Generated filename:', fileName);
      
      const imageRef = ref(storage, `widget-images/${fileName}`);
      console.log('Storage reference created:', imageRef);
      
      // Upload file
      console.log('Uploading file...');
      const snapshot = await uploadBytes(imageRef, file);
      console.log('✅ Upload completed successfully!', snapshot);
      
      // Get download URL
      console.log('Getting download URL...');
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('✅ Download URL obtained successfully:', downloadURL);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading widget image:', error);
      throw error;
    }
  };
  
  // Delete widget image from Firebase Storage
  export const deleteWidgetImage = async (imageUrl) => {
    try {
      if (!imageUrl) return;
      
      // Extract path from Firebase Storage URL
      const pathStart = imageUrl.indexOf('/widget-images/');
      if (pathStart === -1) return; // Not a widget image URL
      
      const pathEnd = imageUrl.indexOf('?');
      const path = imageUrl.substring(pathStart + 1, pathEnd > -1 ? pathEnd : undefined);
      
      const imageRef = ref(storage, path);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('Error deleting widget image:', error);
      throw error;
    }
  };